import React from 'react';
import { Button, addToast } from '@heroui/react';
import { useNavigate } from 'react-router';
import { ProposalData } from '../types';
import { HeartIc, LockRedIc } from '../../../../components/icons';
import { ResponsiveTooltip } from '../../../../components/ui';
import { useAuth } from '../../../../contexts/AuthContext';
import { useAccount } from '../../../../contexts/AccountContext';
import { useTranslations } from '../../../../contexts/translation.context';

interface ActionsCellProps {
  proposal: ProposalData;
  onOrderProposal: (proposal: ProposalData) => void;
  openModal: (price: number) => void;
  isFavorite: boolean;
  onToggleFavorite: (proposalId: string) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = ({
  proposal,
  onOrderProposal,
  openModal,
  isFavorite,
  onToggleFavorite,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { accounts } = useAccount();
  const { t } = useTranslations();

  // Function to get correct queue text with i18n
  const getQueueText = (count: number) => {
    return `${t('proposals.actions.queue')} (${count})`;
  };
  const handleOrderClick = () => {
    // Для подписки цена фиксирована — платим за саму подписку, а не за фанов.
    // Для CPL минимальная стоимость заказа: subs_min * price_per_subscriber.
    const minOrderCost =
      proposal.offerType === 'subscription'
        ? proposal.price.pricePerSubscriber
        : proposal.campaign.targetSubscribers * proposal.price.pricePerSubscriber;

    //@ts-ignore
    if(user && user?.balance < minOrderCost || accounts?.length === 0 || !accounts){
      openModal(minOrderCost)
    } else {
      navigate(`/client/subscribers/proposals/create-order/${proposal.id}`);
    }
  };

  const handleFavoriteClick = () => {
    onToggleFavorite(proposal.id);
  };

  // Проверяем, есть ли у пользователя платные аккаунты для данного оффера
  const hasSuitableAccount = () => {
    // Если в campaign_types есть "free", то подходят любые аккаунты
    if (proposal.campaign_types?.includes('free')) {
      return true;
    }
    // Если в campaign_types нет "free", нужен платный аккаунт
    // Проверяем, есть ли хотя бы один аккаунт с type !== "free"
    return accounts?.some((account) => account.type !== 'free') || false;
  };

  // Determine button state based on proposal status and availability
  const getButtonState = () => {
    // Проверяем наличие подходящего аккаунта
    if (!hasSuitableAccount() && accounts?.length > 0) {
      return {
        variant: 'outline' as const,
        color: '#F31260' as const,
        text: t('proposals.actions.noPaidAccounts'),
        disabled: true,
        icon: <LockRedIc />,
      };
    }

    if (proposal.statistics.applications === 0 && proposal.status === 'active') {
      return {
        variant: 'order' as const,
        color: '#9675CE' as const,
        text: t('proposals.actions.order'),
      };
    }
    if (proposal.status !== 'active') {
      return {
        variant: 'outline' as const,
        color: '#F31260' as const,
        text: t('proposals.actions.noPaidAccounts'),
        disabled: true,
        icon: <LockRedIc />,
      };
    }

    // Check if it's in queue (simulated based on rating)
    if (proposal.statistics.applications > 0) {
      return {
        variant: 'flat' as const,
        color: 'secondary' as const,
        text: getQueueText(proposal?.statistics?.applications || 0),
        disabled: false,
        icon: null,
      };
    }

    return {
      variant: 'solid' as const,
      color: 'primary' as const,
      text: t('proposals.actions.order'),
      disabled: false,
      icon: null,
    };
  };

  const buttonState = getButtonState();
  const isQueue = proposal.status === 'active' && proposal.statistics.applications > 0;
  const isNoPaidAccounts = (!hasSuitableAccount() && accounts?.length > 0) || proposal.status !== 'active';

  const actionButton = (
    <button
      onClick={handleOrderClick}
      disabled={buttonState.disabled}
      className={`
        flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-normal h-8 min-w-[7.5rem] justify-center whitespace-nowrap
        ${buttonState.variant === 'solid' ? 'bg-primary text-white' : ''}
        ${buttonState.variant === 'order' ? 'bg-primary text-white' : ''}
        ${buttonState.variant === 'flat' ? 'bg-[#f1e8ff] text-primary' : ''}
        ${buttonState.variant === 'outline' ? ' border-[0.125rem] border !border-auth-strength-weak !text-auth-strength-weak bg-white' : ''}
        ${buttonState.disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      {buttonState.icon}
      {buttonState.text}
    </button>
  );

  return (
    <div className="flex items-center gap-2 w-full justify-end">
      {isQueue ? (
        <ResponsiveTooltip
          content={t('proposals.actions.queueTooltip')}
          placement="top"
          classNames={{ content: 'max-w-xs' }}
        >
          <div>{actionButton}</div>
        </ResponsiveTooltip>
      ) : isNoPaidAccounts ? (
        <ResponsiveTooltip
          content={t('proposals.actions.noPaidAccountsTooltip')}
          placement="top"
          classNames={{ content: 'max-w-xs' }}
        >
          <div>{actionButton}</div>
        </ResponsiveTooltip>
      ) : (
        actionButton
      )}

      {!isFavorite ? (
        <Button
          //@ts-ignore
          size="xs"
          className="h-8 rounded-2 !w-8 border-[1.6px] border-auth-strength-none p-0 bg-white"
          onPress={handleFavoriteClick}
        >
          <HeartIc fill="#D4D4D8" />
        </Button>
      ) : (
        <Button
          //@ts-ignore
          size="xs"
          className="h-8 rounded-2 !w-8 border-[1.6px] border-auth-strength-weak p-0 bg-auth-strength-weak"
          onPress={handleFavoriteClick}
        >
          <HeartIc fill="#FFFF" />
        </Button>
      )}
    </div>
  );
};

export default ActionsCell;
